import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Ticket, Facebook, Instagram, Youtube, ChevronUp } from 'lucide-react';

export const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { label: 'Shows', path: '/shows' },
    { label: 'About', path: '/about' },
    { label: 'Tour Dates', path: '/tour-dates' },
    { label: 'Media', path: '/media' },
    { label: 'Our Story', path: '/history' },
    { label: 'News', path: '/news' },
    { label: 'Contact', path: '/contact' },
  ];

  // Pages with image/video headers (transparent nav initially)
  const hasImageHeader = location.pathname === '/' || location.pathname.startsWith('/shows/');
  
  // Pages without image headers need blue pill background at top
  const needsBluePillAtTop = !hasImageHeader;
  
  // The pill IS blue when: scrolled OR on a page that needs blue at top
  const isPillBlue = scrolled || needsBluePillAtTop;

  return (
    <header
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'py-2' : 'py-3'
      }`}
    >
          {/* Background - transparent, pill handles its own background */}
          <div className="absolute inset-0 bg-transparent" />
          
          <nav className="max-w-7xl mx-auto relative px-4 lg:px-8">
            {/* Main Nav Container - Always pill shaped, turns blue when scrolled */}
            <div className={`rounded-full px-5 py-2 transition-all duration-500 ${
              isPillBlue
                ? 'bg-brand-primary/95 backdrop-blur-md border border-brand-primary/50 shadow-lg'
                : 'bg-white/10 backdrop-blur-md border border-white/20'
            }`}>
              {/* Top Row - Logo, Nav, CTA */}
              <div className="flex items-center justify-between">
                {/* Logo */}
                <Link 
                  to="/" 
                  data-testid="logo-link"
                  className="relative z-10 group"
                >
                  <img 
                    src={`${process.env.PUBLIC_URL}/logo.png`} 
                    alt="UK Family Theatre Shows" 
                    className="h-16 lg:h-20 w-auto transition-all duration-300 group-hover:scale-105"
                  />
                </Link>

                {/* Desktop Navigation - Button Pills */}
                <div className="hidden lg:flex items-center gap-2">
                  {navLinks.map((link) => {
                    const isActive = location.pathname === link.path;
                    return (
                      <Link
                        key={link.path}
                        to={link.path}
                        data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                        className={`relative px-4 py-2 rounded-full font-body text-sm font-semibold transition-all duration-300 ${
                          isActive
                            ? 'bg-white/20 text-white'
                            : isPillBlue
                              ? 'text-white/90 hover:bg-white/15'
                              : 'text-white/90 hover:bg-white/20'
                        }`}
                      >
                        {link.label}
                      </Link>
                    );
                  })}
                </div>

                {/* CTA Button - Always gold */}
                <div className="hidden lg:flex items-center gap-3">
                  <Link
                    to="/tour-dates"
                    data-testid="nav-book-tickets-btn"
                    className="group inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-bold transition-all duration-300 hover:scale-105 text-sm"
                    style={{ backgroundColor: '#D4AF37', color: '#020120' }}
                  >
                    <Ticket className="w-4 h-4" />
                    Book Tickets
                  </Link>
                </div>

                {/* Mobile Menu Button */}
                <button
                  data-testid="mobile-menu-toggle"
                  onClick={() => setIsOpen(!isOpen)}
                  className="lg:hidden p-3 rounded-xl transition-all duration-300 text-white hover:bg-white/20"
                  aria-label={isOpen ? 'Close menu' : 'Open menu'}
                >
                  {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
              </div>
            </div>
          </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden absolute top-full left-0 right-0 bg-white shadow-2xl rounded-b-3xl mx-4 overflow-hidden"
          >
            <div className="p-6 space-y-2">
              <Link
                to="/"
                data-testid="mobile-nav-home"
                className={`block py-4 px-5 rounded-2xl font-body text-lg font-semibold transition-all ${
                  location.pathname === '/'
                    ? 'bg-brand-primary text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                Home
              </Link>
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link
                    to={link.path}
                    data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`block py-4 px-5 rounded-2xl font-body text-lg font-semibold transition-all ${
                      location.pathname === link.path
                        ? 'bg-brand-primary text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="pt-4"
              >
                <Link
                  to="/tour-dates"
                  data-testid="mobile-book-tickets-btn"
                  className="flex items-center justify-center gap-3 w-full bg-accent-gold text-brand-dark py-5 rounded-2xl font-bold text-lg"
                >
                  <Ticket className="w-5 h-5" />
                  Book Tickets
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export const Footer = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer data-testid="footer" className="bg-brand-dark">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-16">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-8">
              <img 
                src={`${process.env.PUBLIC_URL}/logo.png`} 
                alt="UK Family Theatre Shows" 
                className="h-20 w-auto"
              />
            </Link>
            <p className="font-body text-white/60 leading-relaxed">
              Taking well-known pre-school, family and education brands from page and screen to stage and creating magical memories for the entire family.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-display text-xl font-bold text-white mb-8">Quick Links</h4>
            <ul className="space-y-4">
              {[
                { label: 'Our Shows', path: '/shows' },
                { label: 'Tour Dates', path: '/tour-dates' },
                { label: 'Our Story', path: '/history' },
                { label: 'Latest News', path: '/news' },
                { label: 'Contact Us', path: '/contact' },
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="font-body text-white/60 hover:text-accent-gold transition-colors text-base"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-display text-xl font-bold text-white mb-8">Contact</h4>
            <address className="font-body text-white/60 not-italic space-y-3 text-base">
              <p>Unit 5 & Unit 11, Pontnewynydd Small Business Centre</p>
              <p>Pontnewynydd, Pontypool</p>
              <p>Torfaen, NP4 6AD</p>
              <p className="pt-4">
                <a href="mailto:info@mt-productions.co.uk" className="hover:text-accent-gold transition-colors">
                  info@mt-productions.co.uk
                </a>
              </p>
            </address>
          </div>

          {/* Social */}
          <div>
            <h4 className="font-display text-xl font-bold text-white mb-8">Follow Us</h4>
            <div className="flex gap-4">
              {[
                { icon: Facebook, label: 'Facebook', href: 'https://www.facebook.com/MarkThompsonProductionsLTD' },
                { icon: Instagram, label: 'Instagram', href: 'https://www.instagram.com/mark_Thompson_productions/' },
                { icon: Youtube, label: 'YouTube', href: 'https://www.youtube.com/channel/UCSojjkCbvRneTHkVyIIM76A' },
              ].map(({ icon: Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`social-${label.toLowerCase()}`}
                  className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center text-white/70 hover:bg-accent-gold hover:text-brand-dark transition-all duration-300"
                  aria-label={label}
                >
                  <Icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-6">
          <p className="font-body text-white/50 text-sm">
            © {new Date().getFullYear()} Mark Thompson Productions. All rights reserved.
          </p>
          <div className="flex gap-8">
            <Link to="/media-login" className="font-body text-white/30 hover:text-white/50 text-sm transition-colors">
              Media Assets
            </Link>
            <Link to="/privacy-policy" className="font-body text-white/50 hover:text-accent-gold text-sm transition-colors">
              Privacy Policy
            </Link>
          </div>
        </div>
      </div>

      {/* Scroll to Top */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={scrollToTop}
            data-testid="scroll-to-top"
            className="fixed bottom-8 right-8 w-14 h-14 rounded-2xl bg-accent-gold text-brand-dark flex items-center justify-center shadow-xl hover:bg-white transition-colors focus-ring z-40"
            aria-label="Scroll to top"
          >
            <ChevronUp className="w-6 h-6" />
          </motion.button>
        )}
      </AnimatePresence>
    </footer>
  );
};

export default function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      <main className="flex-grow">
        {children}
      </main>
      <Footer />
    </div>
  );
}
